// src/pages/PricingPage.js
import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { FaRegComments, FaGlobe, FaUserCircle } from "react-icons/fa";

/* ============================================================
   PAGE WRAPPER
============================================================ */

const Page = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 55%, #fef6e4 100%);
  color: var(--text-color);
  padding: 3rem 0 6rem;
`;

/* ============================================================
   HERO
============================================================ */

const Hero = styled.section`
  padding: 3.5rem 10% 2.5rem;
`;

const HeroInner = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 2.5rem;
  align-items: center;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Kicker = styled.p`
  color: var(--primary-color);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 0.7rem;
`;

const Title = styled(motion.h1)`
  font-size: 3.2rem;
  line-height: 1.1;
  margin: 0 0 1rem;

  @media (max-width: 520px) {
    font-size: 2.6rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.15rem;
  color: var(--text-secondary-color);
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 1.6rem;
`;

const HeroCTA = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: var(--text-color);
  font-weight: 800;
  text-decoration: none;
  transition: 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 44px rgba(90, 110, 255, 0.45);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  color: var(--text-secondary-color);
  font-weight: 800;
  text-decoration: none;
  transition: 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
  }
`;

const HeroRight = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 22px;
  padding: 1.6rem;
  backdrop-filter: blur(14px);
`;

const StatTitle = styled.div`
  font-weight: 800;
  color: var(--text-color);
  margin-bottom: 0.6rem;
  font-size: 1.05rem;
`;

const StatText = styled.div`
  color: var(--text-secondary-color);
  line-height: 1.7;
  font-size: 1rem;
`;

/* ============================================================
   SECTION WRAPPER
============================================================ */

const Section = styled.section`
  padding: 3.5rem 10%;
`;

const SectionTitle = styled.h2`
  font-size: 2.3rem;
  margin: 0 0 1rem;
  text-align: center;
`;

const SectionDesc = styled.p`
  color: var(--text-secondary-color);
  max-width: 820px;
  margin: 0 auto 2.6rem;
  line-height: 1.7;
  text-align: center;
`;

/* ============================================================
   FEATURE GRID
============================================================ */

const Grid = styled.div`
  display: grid;
  gap: 1.8rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 1.8rem;
  backdrop-filter: blur(14px);
  text-align: left;
`;

const IconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(90, 110, 255, 0.18);
  border: 1px solid rgba(90, 110, 255, 0.25);
  margin-bottom: 0.9rem;

  svg {
    color: var(--primary-color);
    font-size: 18px;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  margin: 0 0 0.6rem;
`;

const CardText = styled.p`
  margin: 0;
  color: var(--text-secondary-color);
  line-height: 1.7;
`;

/* ============================================================
   HOW IT WORKS
============================================================ */

const Steps = styled.div`
  max-width: 920px;
  margin: 0 auto;
  display: grid;
  gap: 1rem;
`;

const Step = styled.div`
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 1rem;
  align-items: flex-start;
  padding: 1.1rem 1.2rem;
  border-radius: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
`;

const StepNum = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: var(--text-color);
`;

const StepTitle = styled.div`
  font-weight: 800;
  margin-bottom: 0.35rem;
`;

const StepText = styled.div`
  color: var(--text-secondary-color);
  line-height: 1.7;
`;

/* ============================================================
   CTA STRIP
============================================================ */

const CTASection = styled.section`
  padding: 3.5rem 10% 1rem;
`;

const CTABlock = styled.div`
  max-width: 980px;
  margin: 0 auto;
  border-radius: 22px;
  padding: 2.2rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  backdrop-filter: blur(14px);
  text-align: center;
`;

const CTATitle = styled.h2`
  margin: 0 0 0.7rem;
  font-size: 2rem;
`;

const CTAText = styled.p`
  margin: 0 auto 1.4rem;
  color: var(--text-secondary-color);
  max-width: 760px;
  line-height: 1.7;
`;

/* ============================================================
   FAQ
============================================================ */

const FAQWrap = styled.section`
  padding: 3.5rem 10%;
`;

const FAQTitle = styled.h2`
  text-align: center;
  font-size: 2.3rem;
  margin: 0 0 2rem;
`;

const FAQBox = styled.div`
  max-width: 860px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  border-bottom: 1px solid var(--border-color);
  padding: 1.1rem 0;
`;

const Q = styled.h3`
  font-size: 1.15rem;
  margin: 0 0 0.5rem;
`;

const A = styled.p`
  margin: 0;
  color: var(--text-secondary-color);
  line-height: 1.7;
`;

/* ============================================================
   MAIN COMPONENT
============================================================ */

const PricingPage = () => {
  const canonicalUrl = "https://www.trendgenie.io/social";

  const socialSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Trend Genie Social",
    applicationCategory: "SocialNetworkingApplication",
    operatingSystem: "Web",
    url: "https://social.trendgenie.io",
    description:
      "Trend Genie Social is a crypto focused social platform where users share updates, join Web3 discussions, and discover trending topics across blockchain markets, DeFi, and the wider crypto community.",
    publisher: { "@type": "Organization", name: "Trend Genie", url: "https://www.trendgenie.io" },
  };

  return (
    <Page>
      <Helmet>
        <title>Trend Genie Social | Crypto Community Platform for Web3 Discussions</title>
        <meta
          name="description"
          content="Trend Genie Social is a crypto focused social platform where users share updates, join Web3 discussions, and discover trending topics across blockchain markets, DeFi, and the wider crypto community."
        />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(socialSchema)}</script>
      </Helmet>

      {/* HERO */}
      <Hero>
        <HeroInner>
          <div>
            <Kicker>Crypto community platform for Web3</Kicker>

            <Title initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
              Trend Genie Social
            </Title>

            <Subtitle initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              Trend Genie Social is a crypto focused social platform where users share updates, join Web3 discussions, and discover
              what is trending across blockchain markets, DeFi, and the wider crypto community in a clean, focused environment.
            </Subtitle>

            <HeroCTA>
              <PrimaryButton href="https://social.trendgenie.io" target="_blank" rel="noreferrer">
                Open Trend Genie Social
              </PrimaryButton>

              <SecondaryButton href="https://social.trendgenie.io" target="_blank" rel="noreferrer">
                Create an Account
              </SecondaryButton>
            </HeroCTA>
          </div>

          <HeroRight>
            <StatTitle>Built for crypto conversations</StatTitle>
            <StatText>
              Designed for signal to noise. A focused environment for crypto and Web3 conversations, where users can follow market narratives,
              community updates, and discussions without the distraction of general social platforms.
            </StatText>
          </HeroRight>
        </HeroInner>
      </Hero>

      {/* FEATURES */}
      <Section>
        <SectionTitle>Crypto social features for Web3 communities</SectionTitle>
        <SectionDesc>
          A clean, structured experience for crypto communities. Share content, participate in discussion, and discover topics gaining
          attention across blockchain markets, DeFi, and Web3.
        </SectionDesc>

        <Grid>
          <Card initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}>
            <IconWrap><FaRegComments /></IconWrap>
            <CardTitle>Crypto Social Feed</CardTitle>
            <CardText>
              Publish updates, explore community posts, and participate in discussions through reactions and comments in a clean, easy to use feed.
            </CardText>
          </Card>

          <Card initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}>
            <IconWrap><FaGlobe /></IconWrap>
            <CardTitle>Blockchain Trend Discovery</CardTitle>
            <CardText>
              Track conversations gaining momentum across crypto and Web3, so you stay aligned with the narratives and topics the community is actively following.
            </CardText>
          </Card>

          <Card initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}>
            <IconWrap><FaUserCircle /></IconWrap>
            <CardTitle>Web3 Profiles and Community Identity</CardTitle>
            <CardText>
              Build your presence, connect with like minded users, and engage in a community centered on crypto, DeFi, and Web3.
            </CardText>
          </Card>
        </Grid>
      </Section>

      {/* HOW IT WORKS */}
      <Section>
        <SectionTitle>How to use Trend Genie Social</SectionTitle>
        <SectionDesc>
          Get started in minutes. Create a profile, join discussions, and follow crypto topics and Web3 narratives in a focused environment.
        </SectionDesc>

        <Steps>
          <Step>
            <StepNum>1</StepNum>
            <div>
              <StepTitle>Create your account</StepTitle>
              <StepText>Set up a profile and join a crypto focused community built for Web3 discussion.</StepText>
            </div>
          </Step>

          <Step>
            <StepNum>2</StepNum>
            <div>
              <StepTitle>Post and participate</StepTitle>
              <StepText>Share updates, react to posts, and contribute to discussions through comments.</StepText>
            </div>
          </Step>

          <Step>
            <StepNum>3</StepNum>
            <div>
              <StepTitle>Follow what is trending in crypto</StepTitle>
              <StepText>Explore trending discussions across crypto and Web3 to stay updated with the topics and narratives gaining attention.</StepText>
            </div>
          </Step>
        </Steps>
      </Section>

      {/* CTA */}
      <CTASection>
        <CTABlock>
          <CTATitle>Join the crypto conversation on Trend Genie Social</CTATitle>
          <CTAText>
            If you want a crypto focused space for discussion, updates, and market narratives across Web3, Trend Genie Social is built for you.
          </CTAText>

          <PrimaryButton href="https://social.trendgenie.io" target="_blank" rel="noreferrer">
            Open Trend Genie Social
          </PrimaryButton>
        </CTABlock>
      </CTASection>

      {/* FAQ */}
      <FAQWrap>
        <FAQTitle>Trend Genie Social FAQ</FAQTitle>
        <FAQBox>
          <FAQItem>
            <Q>What is Trend Genie Social?</Q>
            <A>
              Trend Genie Social is a crypto focused social platform where users share updates, join discussions, and discover trending topics across blockchain markets, DeFi, and Web3.
            </A>
          </FAQItem>

          <FAQItem>
            <Q>Is Trend Genie Social a mobile app?</Q>
            <A>
              Trend Genie Social is currently available as a web app. You can access it directly at social.trendgenie.io.
            </A>
          </FAQItem>

          <FAQItem>
            <Q>Who is Trend Genie Social designed for?</Q>
            <A>
              Trend Genie Social is designed for crypto traders, DeFi users, Web3 builders, creators, and newcomers who want a focused community platform for discussion and discovery.
            </A>
          </FAQItem>

          <FAQItem>
            <Q>Where do I access Trend Genie Social?</Q>
            <A>
              Use the official link: https://social.trendgenie.io
            </A>
          </FAQItem>
        </FAQBox>
      </FAQWrap>
    </Page>
  );
};

export default PricingPage;

